import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { logoutUser } from "./authSlice";
import { getStoredAuthProfile, isDemoModeSession } from "../utils/auth";
import { authenticatedFetch, buildCanonicalAuthHeaders } from "../utils/authenticatedRequest";
import { isPreschoolUser } from "../utils/preschoolClass";

const isScoreDisabledSession = () => isDemoModeSession() || isPreschoolUser(getStoredAuthProfile());
const DEMO_ATTEMPT_KEY = "BOOST_DEMO_ATTEMPTS";

const getDemoAttemptCount = () => {
  try {
    return Number.parseInt(sessionStorage.getItem(DEMO_ATTEMPT_KEY) || "0", 10) || 0;
  } catch (_error) {
    return 0;
  }
};

const demoPayload = (attempted = getDemoAttemptCount()) => ({
  demoMode: true,
  scoreEnabled: false,
  msg: "Demo mode: score is not saved.",
  practiceData: { score: 0, attempted, total_attempted: attempted, email: "demo" },
});

const initialState = {
  score: 0,
  email: null,
  attempted: 0,
  time: null,
  loading: false,
  error: null,
  demoMode: false,
  scoreEnabled: true,
};

export const fetchPracticeSummary = createAsyncThunk("fetchPracticeSummary", async (_, { rejectWithValue }) => {
  if (isScoreDisabledSession()) return demoPayload();
  const response = await authenticatedFetch(`${process.env.REACT_APP_API_ADDRESS || ""}/api/student/summary`, {
    method: "GET",
    headers: buildCanonicalAuthHeaders(),
  });
  const payload = await response.json().catch(() => ({}));
  return response.ok ? payload : rejectWithValue(payload);
});

const applyPracticeData = (state, action, fallback) => {
  const practiceData = action.payload?.practiceData;
  state.loading = false;
  if (!practiceData) {
    state.error = action.payload?.msg || fallback;
    return;
  }
  state.error = null;
  state.demoMode = Boolean(action.payload?.demoMode);
  state.scoreEnabled = action.payload?.scoreEnabled !== false;
  state.score = Number(practiceData.boosterScore ?? practiceData.score) || 0;
  state.attempted = Number(practiceData.attempted) || 0;
  state.email = practiceData.email;
  state.time = practiceData.updatedAt;
};

const scoreSlice = createSlice({
  name: "score",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(logoutUser.fulfilled, () => initialState)
      .addCase(fetchPracticeSummary.pending, (state) => { state.loading = true; })
      .addCase(fetchPracticeSummary.fulfilled, (state, action) => applyPracticeData(state, action, "Practice summary fetch failed"))
      .addCase(fetchPracticeSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || action.error?.message || "Practice summary fetch failed";
      });
  },
});

export default scoreSlice.reducer;
